import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Send, Bot, User as UserIcon, Sparkles, Check, Repeat } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GREETING = {
  role: 'assistant',
  content:
    'Hoi Erik — ik ben de platform-orchestrator. Vertel wat je wilt: een nieuwe agent bedenken, een terugkerende taak opzetten, of gewoon sparren over hoe we het platform slimmer maken. Ik denk kritisch mee en stel concrete voorstellen voor die jij goedkeurt.',
};

function ProposalCard({ proposal, onApprove, applying }) {
  const isAgent = proposal.kind === 'agent';
  const a = proposal.agent;
  const rt = proposal.recurring_task;
  return (
    <div className="border rounded-lg p-4 bg-card space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className={isAgent ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}>
          {isAgent ? <><Sparkles className="w-3 h-3 mr-1" />Nieuwe agent</> : <><Repeat className="w-3 h-3 mr-1" />Terugkerende taak</>}
        </Badge>
        <span className="text-sm font-medium text-foreground">{proposal.summary}</span>
      </div>
      {isAgent && a && (
        <div className="text-sm text-muted-foreground space-y-1">
          <p><span className="text-muted-foreground">key:</span> <code>{a.key}</code> · <b>{a.name}</b></p>
          {a.description && <p>{a.description}</p>}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">System-prompt</summary>
            <pre className="whitespace-pre-wrap bg-muted rounded p-2 mt-1">{a.system_prompt}</pre>
          </details>
        </div>
      )}
      {!isAgent && rt && (
        <div className="text-sm text-muted-foreground">
          <p><span className="text-muted-foreground">agent:</span> <code>{rt.agent_key}</code> · <span className="text-muted-foreground">cadans:</span> {rt.cadence}</p>
          <p>{rt.rationale}</p>
        </div>
      )}
      <Button size="sm" onClick={() => onApprove(proposal)} disabled={applying} data-testid="approve-proposal-btn">
        {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
        Goedkeuren & aanmaken
      </Button>
    </div>
  );
}

export default function StudioConsole() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([GREETING]);
  const [proposals, setProposals] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [denied, setDenied] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => {
      setProjects(r.data);
      if (r.data[0]) setProjectId(r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, proposals]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setProposals([]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/studio/chat`, {
        messages: next.filter((m) => m !== GREETING).map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages([...next, { role: 'assistant', content: res.data.reply }]);
      setProposals(res.data.proposals || []);
    } catch (e) {
      if (e.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(e.response?.data?.detail || 'Orchestrator antwoordde niet');
        setMessages(next);
      }
    } finally {
      setSending(false);
    }
  };

  const approve = async (proposal) => {
    if (proposal.kind === 'recurring_task' && !projectId) {
      toast.error('Kies eerst een project voor de terugkerende taak');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/studio/apply`, {
        proposals: [proposal],
        project_id: projectId || undefined,
      });
      const madeAgents = res.data.created_agents?.length || 0;
      const madeTasks = res.data.created_tasks?.length || 0;
      toast.success(madeAgents ? `Agent aangemaakt` : madeTasks ? 'Terugkerende taak ingepland' : 'Bestond al');
      setProposals((ps) => ps.filter((p) => p !== proposal));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aanmaken mislukt');
    } finally {
      setApplying(false);
    }
  };

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Alleen voor de eigenaar</CardTitle>
            <CardDescription>De Studio-console is beschikbaar voor de workspace-eigenaar.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')}><ArrowLeft className="w-4 h-4 mr-2" />Terug</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background flex flex-col">
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />Terug
            </Button>
            <div>
              <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
                <Bot className="w-5 h-5" /> Studio — platform-orchestrator
              </h1>
              <p className="text-xs text-muted-foreground">Brainstorm en bouw nieuwe agents & workflows</p>
            </div>
          </div>
          {projects.length > 0 && (
            <select
              className="text-sm border rounded-md px-2 py-1 bg-card"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="studio-project-select"
            >
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        </div>
      </header>

      <main className="container mx-auto px-6 py-6 flex-1 w-full max-w-3xl">
        <div className="space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
              {m.role === 'assistant' && <div className="shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center"><Bot className="w-4 h-4" /></div>}
              <div className={`rounded-2xl px-4 py-2 max-w-[80%] ${m.role === 'user' ? 'bg-blue-600 text-white' : 'bg-card border'}`}>
                <div className="prose prose-sm max-w-none prose-p:my-1">
                  <ReactMarkdown>{m.content}</ReactMarkdown>
                </div>
              </div>
              {m.role === 'user' && <div className="shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center"><UserIcon className="w-4 h-4" /></div>}
            </div>
          ))}

          {proposals.length > 0 && (
            <div className="space-y-3 pl-11">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Voorstellen</p>
              {proposals.map((p, i) => (
                <ProposalCard key={i} proposal={p} onApprove={approve} applying={applying} />
              ))}
            </div>
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center"><Bot className="w-4 h-4" /></div>
              <div className="rounded-2xl px-4 py-3 bg-card border"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </main>

      <div className="border-t bg-card/80 backdrop-blur-sm sticky bottom-0">
        <div className="container mx-auto px-6 py-3 max-w-3xl flex gap-2 items-end">
          <Textarea
            rows={2}
            placeholder="Bijv. 'Ik wil een agent die maandelijks de concurrentie in kaart brengt' of 'Hoe houden we de app veilig?'"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            data-testid="studio-input"
            className="resize-none"
          />
          <Button onClick={send} disabled={sending || !input.trim()} data-testid="studio-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
